import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchAllPdfImages,
    fetchPdfSettings, updatePdfSettings
} from '../../redux/slices/pdfSlice';
import UserTopbar from '../UserTopbar';

import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import { ToastContainer, toast } from "react-toastify";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { usePdf } from '../context/pdfContext';
import { useSearchParams } from 'react-router-dom';
import Apiurl from '../../Helpers/apiUrl';
import axios from 'axios';
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

/* ─── helpers ───────────────────────────────────────────────── */
const parseIds = (val) =>
    String(val ?? '').split(',').map(s => s.trim()).filter(Boolean).map(Number);

const IMG_W = 130;
const IMG_H = 184;

/* ─── DEFAULT_TERMS as HTML for ReactQuill ───────────────────── */
const DEFAULT_TERMS = `<ol>
  <li>This quote is being made on behalf of the channel partner/ company as per agreement.</li>
  <li>This quote is valid for 45 days from the document date or until revised, amended, or modified, whichever comes first.</li>
  <li>Please send your written confirmation towards acceptance of quote/offer within the validity period to the channel partner.</li>
  <li>In case of cancellation of sale after acceptance of offer, customer shall be liable to pay cancellation fee as per below:
    <ul>
      <li>15 days prior to the date of delivery cancellation penalty will be the % of total order value.</li>
      <li>30 days prior to the date of delivery cancellation penalty will be the % of total order value.</li>
      <li>45 days prior to the date of delivery cancellation penalty will be the % of total order value.</li>
    </ul>
  </li>
  <li>The quote can be revised, amended or modified or withdrawn at any time.</li>
  <li>Delivery of material shall be subject to availability of stock with the channel partner/company and the terms mutually agreed.</li>
  <li>Rates are delivered at site.</li>
  <li>Unloading of the goods to be done by the client and not by the supplier.</li>
  <li>The Goods once received needs to be checked and any deviation should be informed within 24 hours of delivery.</li>
  <li>Local levies / GST etc., are as per prevailing rates any change in the same will be applicable at the time of billing and supplies.</li>
  <li>Payment terms 100% advance / Local Letter of Credit / Bank Guarantee.</li>
  <li>Goods once sold will not be taken return, replacement will be applicable as per terms.</li>
  <li>The products are subject to company's warranty policy as detailed on Company's website for India.</li>
  <li>The products quoted can go under product lifecycle change and the latest/replacement product against the same will be supplied.</li>
  <li>All disputes arising in relation to this quote shall be subject to the jurisdiction of courts in New Delhi.</li>
  <li>Customer Care No : 18001024475.</li>
  <li>Signed by ____________</li>
  <li>Quotation is valid for till Date -</li>
  <li>Phone No :</li>
  <li>Office Board line No</li>
</ol>
<p><br></p><p class=\"ql-indent-1\">Thanking you</p>`;

const SECTION_CONFIG = [
    { key: 'coverImage', label: 'Cover Page *', pool: 'coverImages', multi: false },
    { key: 'summaryImage', label: 'Summary Page *', pool: 'summaryImages', multi: false },
    { key: 'technologyImage', label: "Technology / MOT's", pool: 'techImages', multi: true },
    { key: 'communicationImage', label: 'Push Communication', pool: 'communicationImages', multi: true },
    { key: 'selectionImage', label: 'Product Selection *', pool: 'themeImages', multi: false },
    { key: 'termsImage', label: 'Terms & Conditions', pool: 'summaryImages', multi: false },
    { key: 'lastPageImage', label: 'Last Page *', pool: 'lastImages', multi: false },
];

/* ─── ReactQuill toolbar config ──────────────────────────────── */
const QUILL_MODULES = {
    toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['link'],
        ['clean'],
    ],
};

const QUILL_FORMATS = [
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet',
    'indent',
    'align',
    'link',
];

/* ─── Terms Editor Component (ReactQuill) ────────────────────── */
const TermsEditor = ({ value, onChange,  readOnly = false }) => {
    return (
        <div className="rounded-2xl overflow-hidden bg-white/60 backdrop-blur-md border border-white/50 shadow-lg">
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-3 bg-white/40 border-b border-white/50">
                <div className="w-8 h-8 rounded-full bg-white/70 shadow-sm flex items-center justify-center">
                    <EditNoteOutlinedIcon className="text-[#ff7b00]" style={{ fontSize: 18 }} />
                </div>
                <span className="text-sm font-bold text-gray-800 uppercase tracking-wider">
                    Terms &amp; Conditions
                </span>
            </div>

            {/* ReactQuill editor */}
            <div className="quill-terms-wrapper">
                <ReactQuill
                    theme="snow"
                    value={value}
                    onChange={onChange}
                    readOnly={readOnly}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                    placeholder="Type your terms and conditions here…"
                />
            </div>

            {/* Footer hint */}
            <div className="px-5 py-3 bg-white/30 border-t border-white/50">
                <span className="text-[11px] font-medium text-gray-500">
                    Use the toolbar above to format your terms &amp; conditions.
                </span>
            </div>

            {/* Quill height override — scoped so it doesn't bleed globally */}
            <style>{`
                    /* 1. Reset the counter at the top of the editor */
                    .quill-terms-wrapper .ql-editor {
                        counter-reset: term-number;
                    }

                    /* 2. Hide the browser's default numbers which are resetting */
                    .quill-terms-wrapper .ql-editor ol > li {
                        list-style-type: none;
                        position: relative;
                        display: block;
                    }

                    /* 3. Manually inject the incrementing number */
                    .quill-terms-wrapper .ql-editor ol > li::before {
                        content: counter(term-number) ". ";
                        counter-increment: term-number;
                        position: absolute;
                        left: -1.5em; /* Adjust based on your padding */
                        font-weight: 600;
                        color: #374151;
                    }

                    /* 4. Ensure nested bullet points DO NOT increment the number */
                    .quill-terms-wrapper .ql-editor li ul {
                        counter-reset: none;
                        list-style-type: disc;
                        padding-left: 1.5em;
                        margin-bottom: 0.5rem;
                    }

                    .quill-terms-wrapper .ql-editor li ul > li::before {
                        content: none !important; /* Removes the number from sub-bullets */
                        counter-increment: none;
                    }

                    /* Standard editor styling */
                    .quill-terms-wrapper .ql-container {
                        min-height: 380px;
                        font-size: 13.5px;
                        border: none !important;
                    }
                    .quill-terms-wrapper .ql-editor {
                        padding: 16px 40px; /* Extra left padding for our manual numbers */
                    }
                `}</style>
        </div>
    );
};

/* ─── Section Header ──────────────────────────────────────────── */
const SectionHeader = ({ icon, title, badge }) => (
    <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/60 backdrop-blur-md border border-white/50 shadow-sm flex items-center justify-center">
                {React.cloneElement(icon, { className: 'text-[#ff7b00]', style: { fontSize: 20 } })}
            </div>
            <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">{title}</h2>
        </div>
        {badge}
    </div>
);

/* ─── main component ────────────────────────────────────────── */
const PdfSettings = () => {
    const dispatch = useDispatch();
    const { downloadPdf } = usePdf();
    const [searchParams] = useSearchParams();
    const projectIdFromParam = searchParams.get('id');
    const {
        pdfSettings,
        coverImages = [],
        summaryImages = [],
        themeImages = [],
        lastImages = [],
        techImages = [],
        communicationImages = [],
        updateStatus,
        getStatus
    } = useSelector((state) => state.pdfImages);

    const poolMap = { coverImages, summaryImages, themeImages, lastImages, techImages, communicationImages };

    const [columns, setColumns] = useState([]);
    const [images, setImages] = useState({});
    const [terms, setTerms] = useState(DEFAULT_TERMS);
    const [dirty, setDirty] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        if (pdfSettings) {
            setColumns(pdfSettings.data ? [...pdfSettings.data] : []);
            setImages(pdfSettings.images ? { ...pdfSettings.images } : {});
            // If stored value is plain-text (legacy \n style), wrap it in <p> tags
            const stored = pdfSettings.term_condition ?? DEFAULT_TERMS;
            const isHtml = /<[a-z][\s\S]*>/i.test(stored);
            setTerms(isHtml ? stored : `<p>${stored.replace(/\\n/g, '</p><p>')}</p>`);
            setDirty(false);
        }
    }, [pdfSettings]);

    useEffect(() => {
        dispatch(fetchPdfSettings());
        
        // Single consolidated API call for all image categories
        if (!coverImages.length && !summaryImages.length) {
            dispatch(fetchAllPdfImages());
        }
    }, []);

    const handleDownload = async () => {
        if (!projectIdFromParam) return;

        downloadPdf(projectIdFromParam, `${Apiurl}user/downloadPdf`);

        try {
            const token = localStorage.getItem("u_token");
            await axios.post(
                `${Apiurl}project/download-status/${projectIdFromParam}`,
                { pdfCount: 1 },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
        } catch (statusError) {
            console.error("Failed to update download status", statusError);
        }
    };

    const toggleColumn = (idx) => {
        const columnToToggle = columns[idx];
        const activeCount = columns.filter(c => c.value === 1).length;

        if (columnToToggle.value === 0 && activeCount >= 15) {
            toast.warning("Maximum 15 columns can be selected.", {
                position: "bottom-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: "colored",
            });
            return;
        }

        setColumns(prev => prev.map((c, i) => i === idx ? { ...c, value: c.value ? 0 : 1 } : c));
        setDirty(true);
    };

    // const handleImageSelect = (key, id, multi) => {
    //     setImages(prev => {
    //         if (!multi) return { ...prev, [key]: id };
    //         const cur = parseIds(prev[key]);
    //         const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id];
    //         return { ...prev, [key]: next.join(',') };
    //     });
    //     setDirty(true);
    // };

    const handleImageSelect = (key, id, multi) => {
        setImages(prev => {
            if (multi) {
                const cur = parseIds(prev[key]);
                const next = cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id];
                return { ...prev, [key]: next.join(',') };
            }

            if (key === 'termsImage') {
                return {
                    ...prev,
                    [key]: prev[key] === id ? '' : id
                };
            }

            return { ...prev, [key]: id };
        });
        setDirty(true);
    };

    const handleTermsChange = (val) => {
        setTerms(val);
        setDirty(true);
    };

    const handleSave = () => {
        dispatch(updatePdfSettings({ id: pdfSettings.id, images, data: columns, term_condition: terms }));
        setDirty(false);
        setSaved(true);
        setTimeout(() => setSaved(false), 2500);
    };

    const handleReset = () => {
        if (pdfSettings) {
            setColumns([...pdfSettings.data]);
            setImages({ ...pdfSettings.images });
            const stored = pdfSettings.term_condition ?? DEFAULT_TERMS;
            const isHtml = /<[a-z][\s\S]*>/i.test(stored);
            setTerms(DEFAULT_TERMS);
            setDirty(false);
        }
    };

    const loading = getStatus === 'pending' || !pdfSettings || Object.keys(pdfSettings).length === 0 || coverImages.length === 0;
    const activeCount = columns.filter(c => c.value === 1).length;

    const isDownloadDisabled = useMemo(() => {
        const hasColumnSelected = columns.some(col => col.value === 1);

        const hasImage = (key) => {
            const id = images[key];
            return id && id !== 0 && id !== "0" && String(id).trim() !== "";
        };

        const hasRequiredImages =
            hasImage('coverImage') &&
            hasImage('summaryImage') &&
            hasImage('selectionImage') &&
            hasImage('lastPageImage');

        return !hasColumnSelected || !hasRequiredImages;
    }, [columns, images]);

    return (
        <div className="flex flex-col" style={{ height: 'calc(100vh - 64px)' }}>
            <UserTopbar heading="PDF Settings" />
            <ToastContainer
                position="bottom-right"
                autoClose={2000}
                hideProgressBar={true}
            />
            <div className="flex-1 overflow-y-auto">
                <div className="w-full px-8 pt-6 pb-8 max-w-screen-2xl mx-auto space-y-6">

                    {/* ══ SECTION: COLUMN VISIBILITY ══ */}
                    <div>
                        <SectionHeader
                            icon={<ViewColumnOutlinedIcon />}
                            title="PDF Column Visibility"
                            badge={
                                !loading && (
                                    <span className="text-sm text-gray-600 font-medium bg-white/60 px-3 py-1 rounded-full shadow-sm border border-white/50">
                                        <span className="text-[#ff7b00] font-bold">{activeCount}</span>
                                        <span className="text-gray-400 mx-1">/</span>
                                        <span className="text-gray-700">{columns.length} Enabled</span>
                                    </span>
                                )
                            }
                        />

                        <div className="bg-white/60 backdrop-blur-md border border-white/50 rounded-2xl overflow-hidden shadow-lg">
                            {loading ? (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 divide-x divide-y divide-white/30">
                                    {Array(10).fill(0).map((_, i) => (
                                        <div key={i} className="h-16 animate-pulse bg-white/30" />
                                    ))}
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 divide-x divide-y divide-white/30">
                                    {columns.map((col, idx) => {
                                        const active = col.value === 1;
                                        return (
                                            <div
                                                key={col.code}
                                                onClick={() => toggleColumn(idx)}
                                                className={`flex items-center justify-between px-5 py-4 cursor-pointer select-none transition-all duration-200
                                                    ${active
                                                        ? 'bg-white/80 shadow-inner'
                                                        : 'bg-transparent hover:bg-white/30'
                                                    }`}
                                            >
                                                <span className={`text-sm font-semibold leading-tight ${active ? 'text-gray-800' : 'text-gray-500'}`}>
                                                    {col.col_name}
                                                </span>
                                                {active
                                                    ? <ToggleOnIcon className="text-[#ff7b00] flex-shrink-0" style={{ fontSize: 32 }} />
                                                    : <ToggleOffIcon className="text-white flex-shrink-0 drop-shadow-sm" style={{ fontSize: 32 }} />
                                                }
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ══ SECTION: PDF IMAGES ══ */}
                    <div>
                        <SectionHeader
                            icon={<PictureAsPdfOutlinedIcon />}
                            title="PDF Images"
                        />

                        <div className="bg-white/60 backdrop-blur-md border border-white/50 rounded-2xl overflow-hidden shadow-lg divide-y divide-white/30">
                            {SECTION_CONFIG.map(({ key, label, pool, multi }) => {
                                const list = poolMap[pool] ?? [];
                                const selectedIds = parseIds(images[key]);

                                return (
                                    <div key={key} className="flex gap-0">
                                        {/* Left label column */}
                                        <div className="w-52 flex-shrink-0 flex flex-col justify-center px-5 py-5 bg-white/40 border-r border-white/50">
                                            <p className="text-sm font-bold text-gray-700 leading-tight">{label}</p>
                                            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                                {multi ? (
                                                    <span className="text-[10px] font-bold text-[#ff7b00] bg-orange-50/50 border border-[#ff7b00]/30 rounded-full px-2 py-0.5 shadow-sm">
                                                        Multi-select
                                                    </span>
                                                ) : (
                                                    <span className="text-[10px] font-bold text-gray-500 bg-white/50 border border-white/80 rounded-full px-2 py-0.5 shadow-sm">
                                                        Single
                                                    </span>
                                                )}
                                                {selectedIds.length > 0 && selectedIds[0] != 0 && (
                                                    <span className="text-[10px] text-slate-400 font-medium">
                                                        {selectedIds.length} selected
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Right image area */}
                                        <div className="flex-1 px-5 py-5 overflow-x-auto">
                                            {loading ? (
                                                <div className="flex gap-3">
                                                    {[1, 2, 3, 4, 5].map(i => (
                                                        <div key={i} className="rounded-lg animate-pulse bg-slate-100 flex-shrink-0"
                                                            style={{ width: IMG_W, height: IMG_H }} />
                                                    ))}
                                                </div>
                                            ) : list.length === 0 ? (
                                                <p className="text-sm text-slate-400 italic py-2">No images available</p>
                                            ) : (
                                                <div className="flex flex-wrap gap-3">
                                                    {list.map(img => {
                                                        const sel = selectedIds.includes(img.id);
                                                        return (
                                                            <div
                                                                key={img.id}
                                                                onClick={() => handleImageSelect(key, img.id, multi)}
                                                                style={{ width: IMG_W, height: IMG_H }}
                                                                className={`relative overflow-hidden cursor-pointer flex-shrink-0 transition-all duration-300 rounded-lg
                                                                    ${sel
                                                                        ? 'ring-2 ring-[#ff7b00] ring-offset-2 shadow-[0_4px_12px_rgba(255,123,0,0.3)] scale-[1.02]'
                                                                        : 'ring-1 ring-white hover:ring-white/80 hover:shadow-lg'
                                                                    }`}
                                                            >
                                                                    <img
                                                                        src={img.thumb || img.image}
                                                                        alt={img.name}
                                                                        className="w-full h-full object-cover"
                                                                        loading="lazy"
                                                                    />
                                                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent pt-4 pb-1.5 px-2">
                                                                    <p className="text-[10px] font-semibold text-white truncate text-center">
                                                                        {img.name}
                                                                    </p>
                                                                </div>
                                                                {sel && (
                                                                    <div className="absolute top-1.5 right-1.5 bg-white rounded-full leading-none shadow-md">
                                                                        <CheckCircleRoundedIcon className="text-[#ff7b00]" style={{ fontSize: 20 }} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* ══ SECTION: TERMS & CONDITIONS EDITOR ══ */}
                    <div>
                        <SectionHeader
                            icon={<GavelOutlinedIcon />}
                            title="Terms & Conditions"
                        />

                        <div className="shadow-lg rounded-2xl overflow-hidden border border-white/50">
                            {loading ? (
                                <div className="h-[380px] w-full animate-pulse bg-white/30" />
                            ) : (
                                <TermsEditor value={terms} onChange={handleTermsChange} readOnly={false} />
                            )}
                        </div>
                    </div>

                </div>{/* end content */}
            </div>{/* end scrollable area */}

            {/* ══ STICKY SAVE BAR ══ */}
            <div className="flex-shrink-0 bg-white/60 backdrop-blur-lg border-t border-white/50 shadow-[0_-4px_20px_rgba(0,0,0,0.05)] px-8 py-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm min-h-[28px]" />
                <div className="flex items-center gap-3">
                    {projectIdFromParam && (
                        <button
                            onClick={handleDownload}
                            disabled={isDownloadDisabled}
                            className={`flex items-center gap-1.5 px-5 py-2.5 text-xs font-bold rounded-xl transition-all shadow-sm
                                        ${isDownloadDisabled
                                    ? 'text-gray-400 bg-white/40 border-white/30 cursor-not-allowed opacity-60'
                                    : 'text-white bg-[#ff7b00] hover:bg-[#e06a00] shadow-[0_4px_14px_0_rgba(255,123,0,0.39)] hover:shadow-[0_6px_20px_rgba(255,123,0,0.23)] hover:-translate-y-0.5'
                                }`}
                        >
                            <PictureAsPdfOutlinedIcon style={{ fontSize: 18 }} />
                            Download {projectIdFromParam}
                        </button>
                    )}
                    {/* <button
                        onClick={handleReset}
                        className="flex items-center gap-1.5 px-5 py-2.5 text-xs font-bold text-gray-700 bg-white/50 border border-white/50 rounded-xl hover:bg-white shadow-sm disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                        <RestartAltIcon style={{ fontSize: 18 }} />
                        Reset
                    </button> */}
                    <button
                        onClick={handleSave}
                        disabled={updateStatus === 'loading'}
                        className="flex items-center gap-1.5 px-8 py-2.5 text-xs font-bold text-white bg-gray-900 rounded-xl hover:bg-gray-800 shadow-lg active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                    >
                        <SaveOutlinedIcon style={{ fontSize: 18 }} />
                        {updateStatus === 'loading' ? 'Saving…' : 'Save Settings'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PdfSettings;